import { useEffect, useState } from 'react'
import type React from 'react'
import { enableMFA, setupMFA } from '../services/api'
import { errorText } from '../utils/errorText'
import { Modal, Spinner } from './ui'

/**
 * Enrolling an authenticator app: scan the QR code, prove it with a code, save the recovery
 * codes. Two callers: the account page (the user's own choice, through `/auth/mfa/*`) and the
 * sign-in page when the organization requires MFA (through `/auth/login/mfa/*`, where the
 * enable step also opens the session). They differ only in which calls `setup` and `enable`
 * make, so the caller supplies them; the defaults are the account page's.
 */

export interface MfaSetupResult {
  secret: string
  otpauth_uri: string
  qr_data_uri: string | null
}

export interface MfaEnableResult {
  recovery_codes: string[]
}

export default function MfaSetupModal({
  open,
  onClose,
  onEnabled,
  onDone,
  setup = setupMFA,
  enable = enableMFA,
  intro,
}: {
  open: boolean
  /** Closing before the code is accepted: nothing has changed. */
  onClose: () => void
  /** Fired once MFA is live, so the page behind can show the new state. */
  onEnabled: (result: MfaEnableResult) => void
  /** Fired when the recovery codes are acknowledged. Defaults to `onClose`. */
  onDone?: () => void
  setup?: () => Promise<MfaSetupResult>
  enable?: (code: string) => Promise<MfaEnableResult>
  /** Shown above the QR code: why the person is here, when enrolling was not their idea. */
  intro?: React.ReactNode
}) {
  const [step, setStep] = useState<'scan' | 'recovery'>('scan')
  const [secret, setSecret] = useState('')
  const [otpauthUri, setOtpauthUri] = useState('')
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSecret, setShowSecret] = useState(false)

  useEffect(() => {
    if (!open) return
    setStep('scan')
    setCode('')
    setError(null)
    setShowSecret(false)
    setLoading(true)
    setup()
      .then((r) => {
        setSecret(r.secret)
        setOtpauthUri(r.otpauth_uri)
        setQr(r.qr_data_uri)
      })
      .catch((e) => setError(errorText(e, 'Could not start setup.')))
      .finally(() => setLoading(false))
  }, [open])

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setVerifying(true)
    try {
      const result = await enable(code.trim())
      setRecoveryCodes(result.recovery_codes)
      setStep('recovery')
      onEnabled(result)
    } catch (err) {
      setError(errorText(err, 'That code was not accepted.'))
    } finally {
      setVerifying(false)
    }
  }

  const copyCodes = () => {
    navigator.clipboard?.writeText(recoveryCodes.join('\n')).catch(() => {})
  }

  return (
    <Modal
      open={open}
      // Closing mid-enrolment is fine — nothing changed until the code was accepted. Closing on
      // the recovery step is not offered here, so the codes cannot be dismissed by a stray click.
      onClose={step === 'scan' ? onClose : () => {}}
      title={step === 'scan' ? 'Set up two-factor authentication' : 'Save your recovery codes'}
      size="wide"
      footer={
        step === 'scan' ? (
          <>
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              form="mfa-verify"
              className="btn-blue"
              disabled={verifying || code.trim().length < 6}
            >
              {verifying ? 'Verifying…' : 'Verify and enable'}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="btn-outline" onClick={copyCodes}>Copy codes</button>
            <button type="button" className="btn-blue" onClick={onDone ?? onClose}>I have saved them</button>
          </>
        )
      }
    >
      {error && (
        <div className="mb-4 rounded-md border border-red-300 bg-red-100 px-3 py-2 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400">
          {error}
        </div>
      )}

      {step === 'scan' && (
        loading ? (
          <div className="flex justify-center py-10 text-theme-text-tertiary">
            <Spinner className="h-6 w-6" />
          </div>
        ) : (
          <form id="mfa-verify" onSubmit={verify} className="space-y-4">
            {intro}
            <p className="text-sm text-theme-text-secondary">
              Scan this with an authenticator app — Microsoft Authenticator, Google Authenticator,
              1Password — then enter the 6-digit code it shows.
            </p>

            {qr && (
              <div className="flex justify-center">
                <img
                  src={qr}
                  alt="Two-factor authentication setup QR code"
                  className="h-44 w-44 rounded-lg bg-white p-2"
                />
              </div>
            )}

            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowSecret((s) => !s)}
                className="text-xs text-blue-500 hover:text-blue-400"
              >
                {showSecret ? 'Hide setup key' : "Can't scan? Enter the key manually"}
              </button>
              {showSecret && (
                <p className="mt-2 select-all break-all rounded-md bg-theme-tertiary px-3 py-2 font-mono text-xs text-theme-text-primary">
                  {secret}
                </p>
              )}
              {showSecret && !qr && (
                <p className="mt-2 break-all text-[11px] text-theme-text-tertiary">{otpauthUri}</p>
              )}
            </div>

            <div>
              <label className="label" htmlFor="mfa-code">Verification code</label>
              <input
                id="mfa-code"
                className="input text-center text-lg tracking-widest"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="000000"
              />
            </div>
          </form>
        )
      )}

      {step === 'recovery' && (
        <div className="space-y-4">
          <p className="text-sm text-theme-text-secondary">
            Each code signs you in once if you lose your phone. Store them somewhere safe — they
            are shown only now.
          </p>
          <ul className="grid grid-cols-2 gap-2 rounded-lg bg-theme-tertiary p-4 font-mono text-sm text-theme-text-primary">
            {recoveryCodes.map((c) => (
              <li key={c} className="select-all text-center">{c}</li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  )
}
